import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileText, Download, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const fadeUp = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

export default function MyDocumentsPage() {
  const { api, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await api('get', '/documents/my');
      setDocuments(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const downloadDoc = async (doc) => {
    setDownloading(doc.id);
    try {
      const { data } = await api('get', `/documents/${doc.id}/download-url`);
      const link = document.createElement('a');
      link.href = data.data_url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloaded ${data.filename}`);
    } catch {
      toast.error('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const getCategoryLabel = (cat) => {
    if (cat === 'FOH') return 'Front of House';
    if (cat === 'BOH') return 'Back of House';
    return 'All Staff';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-6">
      <motion.div {...fadeUp} className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
          My Documents
        </h1>
        <p className="text-stone-500 mt-1 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Training documents for your role{user?.department ? ` (${user.department})` : ''}
        </p>
      </motion.div>

      <div className="space-y-3" data-testid="my-documents-list">
        {documents.length === 0 ? (
          <Card className="bg-white rounded-2xl border border-stone-200">
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500">No documents available for your department yet</p>
            </CardContent>
          </Card>
        ) : (
          documents.map((doc, i) => (
            <motion.div key={doc.id} {...fadeUp} transition={{ delay: i * 0.04 }}>
              <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-all" data-testid={`my-doc-${doc.id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-[#D9480F]/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-[#D9480F]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {doc.original_filename}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={
                            doc.category === 'FOH' ? 'bg-blue-100 text-blue-700' :
                            doc.category === 'BOH' ? 'bg-orange-100 text-orange-700' :
                            'bg-stone-100 text-stone-600'
                          }>
                            {getCategoryLabel(doc.category)}
                          </Badge>
                          <span className="text-xs text-stone-400">
                            Uploaded {new Date(doc.created_at).toLocaleDateString()} by {doc.uploaded_by_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => downloadDoc(doc)}
                      disabled={downloading === doc.id}
                      className="rounded-xl border-stone-200 shrink-0"
                      data-testid={`download-doc-${doc.id}`}
                    >
                      {downloading === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
